/**
 * Students dashboard section: tabbed list with create/edit collapse.
 */

import * as React from 'react';
import * as Icons from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { StudentsDataTable } from '@/components/students/remote/StudentsDataTable';
import { CollapseReplaceWrapper } from '@/components/common/CollapseReplaceWrapper';
import { Select } from '@/components/forms/Select';
import { CreateStudentView } from '@/components/students/remote/CreateStudentView';
import { EditStudentView } from '@/components/students/remote/EditStudentView';
import { useDetailsView } from '@/hooks/useDetailsView';
import { usePageActions } from '@/hooks/usePageActions';
import { useAuthStore } from '@/stores/authStore';
import { useInterfaceElements } from '@/hooks/useInterfaceElements';
import { onboardingKeys } from '@/hooks/useUsers';
import {
  STUDENT_REMOTE_TAB_LABELS as TAB_LABELS,
  STUDENT_REMOTE_TAB_COLORS as TAB_COLORS,
  STUDENT_REMOTE_TAB_ICONS as TAB_ICONS,
  type StudentRemoteTabView as TabView,
} from '@/components/students/student-configs';
import type { ElementWidth } from '@/types/interface-elements.types';

/** Props for {@link StudentsSection}. */
interface StudentsSectionProps {
  layoutWidth?: ElementWidth;
}

/** Dashboard widget: remote students list. */
export const StudentsSection: React.FC<StudentsSectionProps> = ({ layoutWidth = 'full' }) => {
  const queryClient = useQueryClient();
  const { canCreate, canEditRow, canArchiveRow, canReactivateRow } = usePageActions('students');
  const { user } = useAuthStore();
  const companyId = user?.companyId ?? '';
  const { elements: sidebarElements } = useInterfaceElements('sidebar');
  const { openRowId, openRow, closeRow } = useDetailsView();
  const [activeTab, setActiveTab] = React.useState<TabView>('students');
  /** Which form is mounted in the collapse wrapper. */
  const [activeForm, setActiveForm] = React.useState<'create' | 'edit' | null>(null);
  const [editUserId, setEditUserId] = React.useState<string | null>(null);
  const [editStepKey, setEditStepKey] = React.useState<string | undefined>(undefined);
  const sectionRef = React.useRef<HTMLDivElement>(null);

  /** Close the form and drop the edit record / step so the next open starts clean. */
  const closeForm = React.useCallback(() => {
    setActiveForm(null);
    setEditUserId(null);
    setEditStepKey(undefined);
  }, []);

  /** Close the form and refresh onboarding lists. */
  const handleFormSuccess = React.useCallback(() => {
    setActiveForm(null);
    setEditUserId(null);
    setEditStepKey(undefined);
    void queryClient.invalidateQueries({ queryKey: onboardingKeys.all });
  }, [queryClient]);

  /** Scroll the section to the form when it first opens. */
  const prevActiveFormRef = React.useRef(activeForm);
  React.useEffect(() => {
    if (prevActiveFormRef.current !== activeForm && activeForm !== null && sectionRef.current) {
      sectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    prevActiveFormRef.current = activeForm;
  }, [activeForm]);

  const tabItems = React.useMemo(() => {
    const IconsMap = Icons as unknown as Record<string, Icons.LucideIcon>;
    const menuEl = sidebarElements.find((el) => el.elementId === 'menu-students');
    const MainIcon = IconsMap[menuEl?.config?.['iconName'] as string] ?? Icons.Circle;
    return (Object.keys(TAB_LABELS) as TabView[]).map((id) => {
      const Icon = id === 'students' ? MainIcon : TAB_ICONS[id];
      return {
        id,
        label: TAB_LABELS[id],
        icon: <Icon className="h-4 w-4" />,
        color: TAB_COLORS[id],
      };
    });
  }, [sidebarElements]);

  const tabsNode = React.useMemo(
    () => (
      <Select
        value={activeTab}
        onChange={(id) => {
          closeForm();
          closeRow();
          setActiveTab(id as typeof activeTab);
        }}
        options={tabItems.map((tab) => ({
          value: tab.id,
          label: tab.label,
          icon: tab.icon,
          color: tab.color,
        }))}
        size="sm"
      />
    ),
    [tabItems, activeTab, closeRow, closeForm],
  );

  /** Stable JSX node for the form — avoids unmounting {@link EditStudentView} on re-renders. */
  const formContent = React.useMemo<React.ReactNode>(() => {
    if (activeForm === 'create') {
      return <CreateStudentView onSuccess={handleFormSuccess} onClose={closeForm} />;
    }
    if (activeForm === 'edit' && editUserId) {
      return (
        <EditStudentView
          key={editUserId}
          userId={editUserId}
          stepKey={editStepKey}
          onSuccess={handleFormSuccess}
          onClose={closeForm}
        />
      );
    }
    return null;
  }, [activeForm, editUserId, editStepKey, handleFormSuccess, closeForm]);

  return (
    <div ref={sectionRef}>
      <CollapseReplaceWrapper
        mode="replace"
        isOpen={activeForm !== null}
        collapsePosition="top"
        formContent={formContent}
      >
        <StudentsDataTable
          companyId={companyId}
          expandedRowId={openRowId}
          onToggleRow={openRow}
          onCloseDetails={closeRow}
          onEditStudent={(id) => {
            setEditUserId(id);
            setEditStepKey('registration_information');
            setActiveForm('edit');
            closeRow();
          }}
          enableFilters={true}
          isSection={true}
          layoutWidth={layoutWidth}
          activeTab={activeTab}
          pageActions={{
            canEditRow,
            canViewRow: true,
            canArchiveRow,
            canReactivateRow,
          }}
          showCreateButton={canCreate}
          onCreateStudent={() => {
            setActiveForm('create');
            closeRow();
          }}
          toolbarLeft={tabsNode}
        />
      </CollapseReplaceWrapper>
    </div>
  );
};
